import { Box, styled, Typography, IconButton, TextField, CircularProgress } from '@mui/material';
import { Edit, Save } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { IContact, IBusiness } from '@trii/types/dist/Contacts/contacts';
import { useEditContactApi } from '../../../../context/EditContactApiContext';
import { useTranslationContext } from '../../../../context/TranslationContext';

const NameText = styled(Typography)({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 360,
  textAlign: 'center',
});

const NameWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});

const NameContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  maxWidth: '100%',
  '&:hover .edit-icon': {
    opacity: 1,
    visibility: 'visible',
  },
});

const EditButton = styled(IconButton)<{ alwaysVisible?: boolean }>(({ alwaysVisible }) => ({
  opacity: alwaysVisible ? 1 : 0,
  visibility: alwaysVisible ? 'visible' : 'hidden',
  transition: 'all 0.2s',
  position: 'absolute',
  left: '100%',
  top: '50%',
  transform: 'translateY(-50%)',
}));

export type ContactNameProps = {
  displayName: string;
  contactType?: 'contact' | 'business' | 'unknown';
  firstName?: string;
  lastName?: string;
  name?: string;
  entityId?: string;
  onEntityUpdated?: (data: IContact | IBusiness) => void;
};

const ContactName = ({
  displayName,
  contactType,
  firstName,
  lastName,
  name,
  entityId,
  onEntityUpdated,
}: ContactNameProps) => {
  const api = useEditContactApi();
  const t = useTranslationContext();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    firstName: firstName || '',
    lastName: lastName || '',
    name: name || '',
  });

  useEffect(() => {
    setValues({
      firstName: firstName || '',
      lastName: lastName || '',
      name: name || '',
    });
  }, [firstName, lastName, name]);

  const handleSave = async () => {
    if (!api || !entityId || !contactType || contactType === 'unknown') return;

    setLoading(true);
    try {
      let body: any = {};
      if (contactType === 'contact') {
        body = { firstName: values.firstName, lastName: values.lastName };
      } else if (contactType === 'business') {
        body = { name: values.name };
      }

      const updated = await api.updateEntity({
        entityType: contactType,
        entityId,
        body,
      });

      if (updated && onEntityUpdated) {
        onEntityUpdated(updated);
      }
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update name', err);
    } finally {
      setLoading(false);
    }
  };

  const showEdit = contactType === 'contact' || contactType === 'business';

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        {contactType === 'contact' ? (
          <>
            <TextField
              value={values.firstName}
              onChange={(e) => setValues({ ...values, firstName: e.target.value })}
              size="small"
              placeholder="First Name"
              sx={{ width: 140 }}
            />
            <TextField
              value={values.lastName}
              onChange={(e) => setValues({ ...values, lastName: e.target.value })}
              size="small"
              placeholder="Last Name"
              sx={{ width: 140 }}
            />
          </>
        ) : (
          <TextField
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
            size="small"
            placeholder="Name"
            sx={{ width: 280 }}
          />
        )}
        <IconButton onClick={handleSave} disabled={loading} color="primary">
          {loading ? <CircularProgress size={24} /> : <Save />}
        </IconButton>
      </Box>
    );
  }

  return (
    <NameWrapper>
      <NameContainer>
        <NameText
          variant="h6"
          title={displayName || t('contactName.noName')}
          sx={!displayName ? { color: 'text.disabled', fontStyle: 'italic' } : undefined}
        >
          {displayName || t('contactName.noName')}
        </NameText>
        {showEdit && (
          <EditButton
            size="small"
            onClick={() => setIsEditing(true)}
            className="edit-icon"
            alwaysVisible={!displayName}
            sx={{ ml: 1 }}
          >
            <Edit fontSize="small" />
          </EditButton>
        )}
      </NameContainer>
    </NameWrapper>
  );
};

export default ContactName;
